import React, { useState, useContext } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import Spinner from '../../components/Spinner';
import ReactQuill from 'react-quill-new';
import 'react-quill/dist/quill.snow.css';

const CourseAddPage = () => {
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        description: '',
        author: '',
        imageUrl: '',
        difficulty: 'Beginner',
        estimatedTime: '',
        readingPages: [{ pageNumber: 1, title: '', content: '' }],
        quizId: '',
        category: 'General',
        tags: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const { token } = useContext(AuthContext);

    const onChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handlePageTitleChange = (index, value) => {
        const updatedPages = [...formData.readingPages];
        updatedPages[index].title = value;
        setFormData({ ...formData, readingPages: updatedPages });
    };

    const handlePageContentChange = (index, value) => {
        const updatedPages = [...formData.readingPages];
        updatedPages[index].content = value;
        setFormData({ ...formData, readingPages: updatedPages });
    };

    const addReadingPage = () => {
        const nextPageNumber = formData.readingPages.length + 1;
        setFormData({
            ...formData,
            readingPages: [...formData.readingPages, { pageNumber: nextPageNumber, title: '', content: '' }]
        });
    };

    const removeReadingPage = (index) => {
        if (formData.readingPages.length <= 1) {
            return toast.error('Course must have at least one reading page.');
        }
        let updatedPages = formData.readingPages.filter((_, i) => i !== index);
        updatedPages = updatedPages.map((page, i) => ({ ...page, pageNumber: i + 1 }));
        setFormData({ ...formData, readingPages: updatedPages });
    };

    const onSubmit = async e => {
        e.preventDefault();
        setSubmitting(true);
        const loadingToast = toast.loading('Creating course...');
        try {
            const config = { headers: { 'Content-Type': 'application/json', 'x-auth-token': token } };
            await axios.post(`${API_BASE_URL}/api/courses`, formData, config);
            toast.success('Course successfully created!', { id: loadingToast });
            navigate('/admin/courses');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Failed to create course. Please check all fields.', { id: loadingToast });
            setSubmitting(false);
        }
    };
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline','strike', 'blockquote'],
            [{'list': 'ordered'}, {'list': 'bullet'}],
            ['link', 'image'], ['clean']
        ],
    };

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card p-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold">Add New Course</h1>
                <button 
                    type="button" 
                    onClick={() => navigate('/admin/courses')} 
                    className="btn btn-secondary"
                >
                    ← Back to Course Management
                </button>
            </div>
            <form onSubmit={onSubmit} className="space-y-6">
                <input type="text" name="title" placeholder="Course Title" value={formData.title} onChange={onChange} required className="input-field !text-xl !font-bold" />
                <input type="text" name="slug" placeholder="URL Slug (Optional - e.g., introduction-to-aerodynamics)" value={formData.slug} onChange={onChange} className="input-field text-sm" />
                <textarea name="description" placeholder="Course Description" value={formData.description} onChange={onChange} required rows="4" className="input-field"></textarea>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <select name="difficulty" value={formData.difficulty} onChange={onChange} required className="input-field">
                        <option value="Beginner">Beginner Level</option>
                        <option value="Intermediate">Intermediate Level</option>
                        <option value="Advanced">Advanced Level</option>
                    </select>
                    <input type="text" name="estimatedTime" placeholder="Estimated Time (e.g., 3 Hours)" value={formData.estimatedTime} onChange={onChange} className="input-field" />
                     <input type="text" name="category" placeholder="Category (e.g., Aircraft Engines)" value={formData.category} onChange={onChange} required className="input-field" />
               </div>
                 <input type="url" name="imageUrl" placeholder="Cover Image URL (https://...)" value={formData.imageUrl} onChange={onChange} className="input-field" />
                 <input type="text" name="tags" placeholder="Tags (comma separated)" value={formData.tags} onChange={onChange} className="input-field" />
                 <input type="text" name="quizId" placeholder="Related Quiz ID (Optional)" value={formData.quizId} onChange={onChange} className="input-field" />
                 <input type="text" name="author" placeholder="Author (Leave blank for default)" value={formData.author} onChange={onChange} className="input-field" />

                <div className="space-y-4 pt-4 border-t border-light-border dark:border-dark-border">
                    <h2 className="text-2xl font-semibold">Reading Pages</h2>
                    {formData.readingPages.map((page, index) => (
                        <div key={index} className="p-4 border rounded-lg bg-light-bg dark:bg-dark-bg relative">
                             <span className="absolute top-2 left-2 text-xs font-bold opacity-50">Page {page.pageNumber}</span>
                            <button
                                type="button"
                                onClick={() => removeReadingPage(index)}
                                className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-sm"
                                title="Delete this page"
                            >
                                Delete
                            </button>
                            <input
                                type="text"
                                placeholder={`Page ${page.pageNumber} Title`}
                                value={page.title}
                                onChange={(e) => handlePageTitleChange(index, e.target.value)}
                                required
                                className="input-field !text-lg !font-semibold mb-3 mt-4"
                            />
                            <div className="h-[250px] mb-12 md:mb-0">
                                <ReactQuill
                                    theme="snow"
                                    value={page.content}
                                    onChange={(value) => handlePageContentChange(index, value)}
                                    modules={quillModules}
                                    className="h-full bg-light-card dark:bg-dark-card"
                                />
                            </div>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addReadingPage}
                        className="w-full py-2 border-dashed border-2 rounded-md hover:bg-light-bg dark:hover:bg-dark-border transition-colors mt-4"
                    >
                        + Add New Reading Page
                    </button>
                </div>

                <button type="submit" disabled={submitting} className="btn-primary w-full !text-lg mt-6">
                    {submitting ? <Spinner size='h-5 w-5 border-b-2' /> : 'Create Course'}
                </button>
            </form>
        </motion.div>
    );
};

export default CourseAddPage;